import classnames from "classnames"
import { motion } from "framer-motion"
import isFunction from "lodash/isFunction"
import PropTypes from "prop-types"
import { useEffect, useRef } from "react"
import "./Overlay.css"

// Approximate Velocity UI Pack enter transitions used by Overlay callers.
const ENTER_TRANSITIONS = {
  "transition.fadeIn": {
    initial: { opacity: 0 },
    animate: { opacity: 1 }
  },
  "transition.slideUpIn": {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 }
  },
  "transition.slideLeftIn": {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 }
  },
  "transition.slideRightIn": {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 }
  }
}

const Overlay = ({
  placement = "bottom",
  alignment = "right",
  show = false,
  children,
  onClick,
  enterTransition,
  enterDuration = 150
}) => {
  const ref = useRef()

  useEffect(() => {
    if (show) {
      // Only scrolls the overlay if necessary, since using "nearest" option.
      ref.current.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }
  }, [show])

  const className = classnames(
    "Overlay",
    `Overlay--place-${placement}`,
    `Overlay--align-${alignment}`
  )

  const enterMotion = enterTransition
    ? ENTER_TRANSITIONS[enterTransition]
    : null

  return (
    <div ref={ref} className={className}>
      {show &&
        (enterMotion ? (
          <motion.div
            initial={enterMotion.initial}
            animate={enterMotion.animate}
            transition={{ duration: enterDuration / 1000, ease: "easeOut" }}
            onClick={(e) => isFunction(onClick) && onClick(e)}
          >
            {children}
          </motion.div>
        ) : (
          <div onClick={(e) => isFunction(onClick) && onClick(e)}>
            {children}
          </div>
        ))}
    </div>
  )
}

Overlay.displayName = "Overlay"

Overlay.propTypes = {
  // Should overlay be shown
  show: PropTypes.bool,
  // Should clicks inside overlay bubble to OverlayTrigger
  preventClickPropagation: PropTypes.bool,
  // Name of enter transition (Velocity UI Pack name, mapped to framer-motion)
  enterTransition: PropTypes.oneOf(Object.keys(ENTER_TRANSITIONS)),
  // Speed of enter transition (ms)
  enterDuration: PropTypes.number,
  // Where overlay should be positioned relative to container
  placement: PropTypes.oneOf(["top", "right", "bottom", "left"]),
  // Which overlay edge should align with container
  alignment: PropTypes.oneOf(["center", "top", "right", "bottom", "left"])
}

export default Overlay
